import { useState } from "react";
import { Wand2, Loader2, Copy, Camera } from "lucide-react";
import { toast } from "sonner";
import { evotoRecipe } from "../lib/api";
import { T } from "@/locales/tr";

const PRESETS = [
  { v: "linkedin-pro", l: "Portrait LinkedIn — pro & lumineux" },
  { v: "editorial-paris", l: T("Éditorial parisien — sobre & chic") },
  { v: "mode-magazine", l: "Mode magazine — couverture" },
  { v: "papicoo-signature", l: T("Signature papicoo.fr — naturel & contraste maîtrisé") },
];

export default function Evoto() {
  const [description, setDescription] = useState("");
  const [objective, setObjective] = useState("papicoo-signature");
  const [aesthetic, setAesthetic] = useState("Inspiré de papicoo.fr — peau respectée, lumière naturelle, contraste maîtrisé");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!description.trim()) { toast.error(T("Décris ta photo (sujet, lumière, lieu).")); return; }
    setLoading(true); setResult("");
    try {
      const data = await evotoRecipe({ description, objective, aesthetic_hint: aesthetic });
      setResult(data.text);
      toast.success(T("Recette Évoto prête."));
    } catch { toast.error(T("Échec de la génération.")); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="page-evoto" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Recettes de retouche · Évoto")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Tes presets Évoto.")}<br />
        <span className="italic font-light">{T("Au goût papicoo.fr.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-2xl leading-relaxed">
        {T("Décris ta photo, choisis un objectif. Papicoo génère une")} <strong>{T("recette précise")}</strong> {T("à appliquer en quelques clics dans")} <strong>{T("Évoto")}</strong> {T(": peau, yeux, dents, lumière, color grading, fond — au standard de qualité de papicoo.fr.")}
      </p>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="papicoo-card p-6 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Décris la photo")}</label>
            <textarea
              data-testid="evoto-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder={T("Ex : Portrait studio d'une femme, peau mate, lumière clé latérale, fond gris foncé, costume crème.")}
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Objectif")}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.v}
                  data-testid={`evoto-preset-${p.v}`}
                  onClick={() => setObjective(p.v)}
                  className={`px-3 py-2 text-xs rounded-full ${objective === p.v ? "papicoo-btn-gold" : "bg-white border border-[color:var(--papicoo-border)]"}`}
                >
                  {T(p.l)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Esthétique cible")}</label>
            <input
              data-testid="evoto-aesthetic"
              value={aesthetic}
              onChange={(e) => setAesthetic(e.target.value)}
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
          </div>
          <button data-testid="evoto-go-btn" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            {loading ? T("Préparation…") : T("Générer la recette Évoto")}
          </button>
        </div>

        <div data-testid="evoto-result">
          {result ? (
            <div className="papicoo-card p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-2xl text-[color:var(--papicoo-ink)]">{T("Recette Évoto")}</div>
                <button data-testid="evoto-copy" onClick={() => { navigator.clipboard.writeText(result); toast.success(T("Copié.")); }} className="text-xs inline-flex items-center gap-1 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]">
                  <Copy size={14} /> {T("Copier")}
                </button>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-[15px] leading-relaxed">{result}</pre>
            </div>
          ) : (
            <div className="papicoo-card p-6 text-sm italic text-[color:var(--papicoo-muted)] inline-flex items-start gap-3">
              <Camera size={18} className="text-[color:var(--papicoo-gold)] mt-1" />
              <span>{T("La recette s'affichera ici, avec les pourcentages exacts pour la peau, les yeux, la lumière, le color grading.")}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
